import { cnpj, cpf } from 'cpf-cnpj-validator';
import moment from 'moment';
import i18n from './i18n';

const isEmpty = (value) => {
	return value === undefined || value === null || value === '';
};

const rules = {
	required: (value) => !isEmpty(value) || i18n.t('COMMON__REQUIRED_FIELD'),
	arrayRequired: (value) =>
		!isEmpty(value) || value.length > 0 || i18n.t('COMMON__REQUIRED_FIELD'),
	arrayNotEmpty: (value) =>
		(!isEmpty(value) && value.length > 0) ||
		i18n.t('COMMON__FIELD_MUST_BE_FILLED'),
	email: (value) => {
		const pattern =
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		return isEmpty(value) || pattern.test(value) || 'E-mail inválido';
	},
	minlength: (value, length) =>
		isEmpty(value) ||
		value.length >= length ||
		i18n.t('COMMON__MINIMUM_CHARACTERS'),
	maxlength: (value, length) =>
		isEmpty(value) ||
		value.length <= length ||
		i18n.t('COMMON__MAXIMUM_CHARACTERS'),
	minDate: (startDate, endDate) => {
		if (endDate < startDate) {
			return `${i18n.t('COMMON__DATE_AFTER__OR__EQUAL_TO')} ${moment(
				startDate
			).format('DD/MM/YYYY')}`;
		}

		if (isEmpty(startDate) && !isEmpty(endDate)) {
			return `${i18n.t('COMMON__DATE_END_NOT_BE_BIGGER_THAN_START')}`;
		}

		return true;
	},
	getMaxMaintenanceStartDate: (startDate, endDate) => {
		if (endDate < startDate) {
			return `${i18n.t('COMMON__DATE_AFTER__OR__EQUAL_TO')} ${moment(
				startDate
			).format('DD/MM/YYYY')}`;
		}
	},
	getMaxMaintenanceDate: (warrantyStartDate, startDate) => {
		if (startDate) {
			if (startDate > warrantyStartDate) {
				return `${i18n.t('COMMON__DATE_AFTER__OR__EQUAL_TO')}`;
			}
		}
		return true;
	},
	getMaxDateValue: (startDate, endDate, maxDateValue, notRequired) => {
		if (
			!(startDate === moment().format('YYYY-MM-DD') || endDate <= maxDateValue)
		) {
			return `${i18n.t('COMMON__DATE_EQUAL_TO')} `;
		}

		if (isEmpty(endDate) && !isEmpty(startDate)) {
			return `${i18n.t('COMMON__DATE_BEFORE__OR__EQUAL_TO')}`;
		}

		if (endDate > maxDateValue || endDate < startDate) {
			return `${i18n.t('COMMON__DATE_BEFORE__OR__EQUAL_TO')} `;
		}

		if (isEmpty(endDate) && isEmpty(startDate) && !notRequired) {
			return i18n.t('COMMON__REQUIRED_FIELD');
		}

		return true;
	},
	cpf: (value) =>
		isEmpty(value) || cpf.isValid(value) || i18n.t('COMMON__INVALID_CPF'),
	cnpj: (value) =>
		isEmpty(value) || cnpj.isValid(value) || i18n.t('COMMON__INVALID_CNPJ'),
	year: (value) =>
		isEmpty(value) ||
		new RegExp(/^(19|20)\d{2}/).test(value) ||
		i18n.t('COMMON__INVALID_YEAR'),
	fixedSizeNumber: (digits) => {
		return (value) => {
			//'^.{' + digits + ',6}$'
			let regex = '\\d{' + digits + '}';
			return (
				isEmpty(value) ||
				new RegExp(regex).test(value) ||
				i18n.t('COMMON__INVALID_NUMBER')
			);
		};
	},
	number: (value) =>
		isEmpty(value) || /(^\d+$)/.test(value) || i18n.t('COMMON__INVALID_NUMBER'),
	slug: (value, message) =>
		isEmpty(value) ||
		/^[a-z0-9\-._]+$/.test(value) ||
		message ||
		i18n.t('COMMON__INVALID_VALUE'),
	strongPassword: (value) => {
		let result =
			/[a-z]/.test(value) || i18n.t('COMMON__PASSWORD_LOWERCASE_LETTERS');

		if (result === true) {
			result =
				/[A-Z]/.test(value) || i18n.t('COMMON__PASSWORD_UPPERCASE_LETTERS');
		}

		if (result === true) {
			result = /[0-9]/.test(value) || i18n.t('COMMON__PASSWORD_NUMBERS');
		}

		if (result === true) {
			result =
				/\W|_/.test(value) || i18n.t('COMMON__PASSWORD_SPECIAL_CHARACTERS');
		}

		if (result === true) {
			result =
				value.length >= 8 || i18n.t('COMMON__PASSWORD_MINIMUM_CHARACTERS');
		}

		return result;
	},

	fileSize: (size) => {
		return (file) => {
			const sizeMB = size * 1024 * 1024;
			return (
				!file ||
				file.size <= sizeMB ||
				`${i18n.t('COMMON__FILE_LESS_THAN')} ${size} MB`
			);
		};
	},

	filesSize: (size) => {
		return (files) => {
			const sizeMB = size * 1024 * 1024;
			return (
				!files ||
				!files.length ||
				files.reduce((sum, file) => (sum += file.size), 0) <= sizeMB ||
				`${i18n.t('COMMON__FILE__SIZE__LESS_THAN')} ${size} MB`
			);
		};
	},
};

const masks = {
	cpf: '###.###.###-##',
	cnpj: '##.###.###/####-##',
	zipCode: '#####-###',
	year: '####',
	phone: '(##) #####-####',
};

export default {
	install(Vue) {
		Vue.prototype.$rules = rules;
		Vue.prototype.$masks = masks;
	},
};
